/**
 * Module dependencies.
 */

import { cn } from 'app/lib/utils';
import { Editor } from '@tiptap/react';
import {
  CheckSquare,
  Code2,
  Heading1,
  Heading2,
  Heading3,
  Image as ImageIcon,
  List,
  ListOrdered,
  LucideIcon,
  Minus,
  Quote,
  Table
} from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * `SlashHelpers` type — side effects a block can trigger outside the editor.
 */

export type SlashHelpers = {
  pickImage: () => void;
};

/**
 * `SlashItem` type.
 */

export type SlashItem = {
  icon: LucideIcon;
  key: string;
  keywords: string;
  run: (editor: Editor, helpers: SlashHelpers) => void;
};

/**
 * Export `SLASH_ITEMS` — blocks offered by the `/` menu. Labels come from the
 * `notes.editor.blocks.<key>` translations.
 */

export const SLASH_ITEMS: SlashItem[] = [
  {
    icon: Heading1,
    key: 'heading1',
    keywords: 'heading title h1',
    run: editor => editor.chain().focus().toggleHeading({ level: 1 }).run()
  },
  {
    icon: Heading2,
    key: 'heading2',
    keywords: 'heading subtitle h2',
    run: editor => editor.chain().focus().toggleHeading({ level: 2 }).run()
  },
  {
    icon: Heading3,
    key: 'heading3',
    keywords: 'heading h3',
    run: editor => editor.chain().focus().toggleHeading({ level: 3 }).run()
  },
  {
    icon: List,
    key: 'bulletList',
    keywords: 'bullet unordered list',
    run: editor => editor.chain().focus().toggleBulletList().run()
  },
  {
    icon: ListOrdered,
    key: 'orderedList',
    keywords: 'numbered ordered list',
    run: editor => editor.chain().focus().toggleOrderedList().run()
  },
  {
    icon: CheckSquare,
    key: 'taskList',
    keywords: 'todo task checkbox check',
    run: editor => editor.chain().focus().toggleTaskList().run()
  },
  {
    icon: Quote,
    key: 'quote',
    keywords: 'quote blockquote',
    run: editor => editor.chain().focus().toggleBlockquote().run()
  },
  {
    icon: Code2,
    key: 'codeBlock',
    keywords: 'code snippet fenced',
    run: editor => editor.chain().focus().toggleCodeBlock().run()
  },
  {
    icon: Table,
    key: 'table',
    keywords: 'table grid',
    run: editor => editor.chain().focus().insertTable({ cols: 3, rows: 3, withHeaderRow: true }).run()
  },
  {
    icon: Minus,
    key: 'divider',
    keywords: 'divider separator rule hr',
    run: editor => editor.chain().focus().setHorizontalRule().run()
  },
  {
    icon: ImageIcon,
    key: 'image',
    keywords: 'image picture photo upload',
    run: (editor, helpers) => helpers.pickImage()
  }
];

/**
 * `SlashState` type — an open `/query` token and where to draw the menu.
 */

export type SlashState = {
  from: number;
  left: number;
  query: string;
  to: number;
  top: number;
};

/**
 * Export `filterSlashItems`.
 */

export function filterSlashItems(query: string, labelOf: (item: SlashItem) => string): SlashItem[] {
  const needle = query.trim().toLowerCase();

  if (!needle) {
    return SLASH_ITEMS;
  }

  return SLASH_ITEMS.filter(item => `${labelOf(item)} ${item.keywords}`.toLowerCase().includes(needle));
}

/**
 * Export `detectSlash` — returns the `/query` token when the caret sits right
 * after one at the start of a paragraph, positioned relative to `container`.
 */

export function detectSlash(editor: Editor, container: HTMLElement | null): SlashState | null {
  const { selection } = editor.state;

  if (!selection.empty || !container) {
    return null;
  }

  const { $from } = selection;

  if ($from.parent.type.name !== 'paragraph') {
    return null;
  }

  const textBefore = $from.parent.textBetween(0, $from.parentOffset, undefined, '￼');
  const match = /^\/([^\s/]*)$/.exec(textBefore);

  if (!match) {
    return null;
  }

  const from = $from.pos - match[0].length;
  const coords = editor.view.coordsAtPos(from);
  const rect = container.getBoundingClientRect();
  const menuHeight = 280;
  const below = coords.bottom - rect.top + container.scrollTop + 6;
  const fitsBelow = coords.bottom + menuHeight < rect.bottom;

  return {
    from,
    left: Math.max(0, Math.min(coords.left - rect.left, rect.width - 240)),
    query: match[1],
    to: $from.pos,
    top: fitsBelow ? below : coords.top - rect.top + container.scrollTop - menuHeight - 6
  };
}

/**
 * `Props` type.
 */

type Props = {
  index: number;
  items: SlashItem[];
  onHover: (index: number) => void;
  onSelect: (item: SlashItem) => void;
  state: SlashState;
};

/**
 * Export `SlashMenu` component.
 */

export function SlashMenu({ index, items, onHover, onSelect, state }: Props): JSX.Element {
  const [translate] = useTranslation();
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    listRef.current?.children[index]?.scrollIntoView({ block: 'nearest' });
  }, [index]);

  return (
    <ul
      className={
        'absolute z-30 max-h-[280px] w-[240px] overflow-y-auto rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg'
      }
      ref={listRef}
      role={'listbox'}
      style={{ left: state.left, top: state.top }}
    >
      {items.length === 0 && (
        <li className={'px-2 py-1.5 text-sm text-muted-foreground'}>{translate('notes.editor.slash.noResults')}</li>
      )}

      {items.map((item, itemIndex) => {
        const Icon = item.icon;

        return (
          <li
            aria-selected={itemIndex === index}
            className={cn(
              'flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm',
              itemIndex === index ? 'bg-muted text-foreground' : 'text-foreground/90'
            )}
            key={item.key}
            onMouseDown={event => {
              event.preventDefault();
              onSelect(item);
            }}
            onMouseEnter={() => onHover(itemIndex)}
            role={'option'}
          >
            <span
              className={
                'grid h-7 w-7 shrink-0 place-items-center rounded-md border border-border bg-background text-muted-foreground [&_svg]:h-4 [&_svg]:w-4'
              }
            >
              <Icon />
            </span>

            {translate(`notes.editor.blocks.${item.key}`)}
          </li>
        );
      })}
    </ul>
  );
}
